import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { Vehicle } from 'core/models/vehicle';
import { CombinedVehicleController } from 'infrastructure/controller/CombinedVehicleController';
import { GamingWheelVehicleController } from 'infrastructure/controller/GamingWheelController';
import { KeyboardVehicleController } from 'infrastructure/controller/keyboardController';
import { useWithVehicleController } from 'services/useVehicleControllerStatus';
import { useVehicleControllerBind } from 'services/useVehicleControllerBind';
import { useSelectedProject } from 'services/useSelectedProject';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { useEightViewsForDriveModeLayout } from './useEightViewsForDriveModeLayout';
import { DriveModeLayout } from 'components/views/molecules/DriveModeLayout';
import { AdjustedVehicleController } from 'infrastructure/controller/AdjustedVehicleController';
import { useVehicleConfiguration } from 'services/useVehicleConfiguration';
import { useAlertTicker, useWithAlertManager } from 'services/useAlertTicker';
import { useSessionController } from 'services/oidc/useSessionController';
import { useVehicleStatusError } from 'services/useVehicleStatus';
import { useMediaDeviceStatusError } from 'services/useMediaDeviceStatus';
import { useEmergencyButtons } from 'services/useEmergencyButtons';
import { useEnvironments } from 'services/useEnvironments';
import { useSelectedEnvironment } from 'services/useSelectedEnvironment';
import { ignore } from 'utility/ignore';

export type DrivePageWithProjectProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
}

export const DrivePageWithProject = (props: DrivePageWithProjectProps) => {
    const { vehicleId, projectId, environmentId } = props;

    const [vehicleConfig] = useVehicleConfiguration(vehicleId);
    const controller = useMemo(() => {
        return new AdjustedVehicleController(vehicleConfig, new CombinedVehicleController([
            new GamingWheelVehicleController(),
            new KeyboardVehicleController(),
        ]));
    }, [vehicleConfig]);

    useVehicleControllerBind(vehicleId, projectId, environmentId, controller);

    const withVehicleController = useWithVehicleController();

    const { alerting, messages: alertMessages, addAlertMessage } = useAlertTicker();

    const sessionController = useSessionController();
    useEffect(() => {
        const listener = () => {
            addAlertMessage('Your access token has been invalidated. Please sign-in again!!');
        };
        sessionController.addEventListener('revoke', listener);
        return () => sessionController.removeEventListener('revoke', listener);
    }, [sessionController, addAlertMessage]);

    const vehicleStatusError = useVehicleStatusError();
    useEffect(() => {
        if (vehicleStatusError) {
            addAlertMessage(vehicleStatusError.message);
        }
    }, [vehicleStatusError, addAlertMessage]);

    const mediaDeviceStatusError = useMediaDeviceStatusError();
    useEffect(() => {
        if (mediaDeviceStatusError) {
            addAlertMessage(mediaDeviceStatusError.message);
        }
    }, [mediaDeviceStatusError, addAlertMessage]);

    const { views, controllerPanel } = useEightViewsForDriveModeLayout(vehicleId, projectId, environmentId, alerting, alertMessages);

    const vehicleIds = useMemo(() => [vehicleId], [vehicleId]);
    const emergencyButtonsError = useEmergencyButtons(projectId, vehicleIds);
    useEffect(() => {
        if (!emergencyButtonsError) return;
        if (emergencyButtonsError instanceof TypeError) {
            console.info('No UI server found.');
        } else {
            console.error(emergencyButtonsError);
        }
    }, [emergencyButtonsError]);

    return withVehicleController(controller, vehicleId)(
        <DriveModeLayout
            views={views}
            controllerPanel={controllerPanel}
        />
    );
};

export type DrivePageProps = {}

export function DrivePage(props: DrivePageProps) {
    ignore(props);

    const withAlertManager = useWithAlertManager();

    const { vehicleId } = useParams<{ vehicleId: string }>();
    if (vehicleId === undefined) throw Error('missing vehicle ID in URL');

    const projectId = useSelectedProject()?.id;
    const environments = useEnvironments(projectId);
    const environmentId = useSelectedEnvironment(environments)?.id;
    if (!projectId || !environmentId) {
        return <p>Please select project and environment</p>;
    }

    return withAlertManager(
        <DrivePageWithProject vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} />
    );
}
